import { useEffect, useState } from 'react';

/** Événement Chromium (Android, Chrome/Edge desktop) permettant de déclencher l'installation. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Capturé dès le chargement du module : le navigateur peut émettre l'événement avant le montage du composant
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // pas de mini-barre automatique : l'app propose son propre bouton
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true);

/** iPhone / iPad (y compris iPadOS qui se présente comme un Mac tactile) : pas d'invite, ajout via « Partager ». */
const isIos = (): boolean =>
  typeof navigator !== 'undefined' &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export interface PwaInstall {
  /** Déjà ouverte en tant qu'application installée */
  installed: boolean;
  /** Le navigateur propose l'installation (Android, Chrome, Edge…) : appeler promptInstall() */
  canPrompt: boolean;
  /** Safari iOS/iPadOS : installation manuelle (Partager → « Sur l'écran d'accueil ») */
  iosManual: boolean;
  promptInstall: () => Promise<boolean>;
}

export const usePwaInstall = (): PwaInstall => {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const installed = isStandalone();
  return {
    installed,
    canPrompt: !installed && deferred !== null,
    iosManual: !installed && isIos(),
    promptInstall: async () => {
      if (!deferred) return false;
      const ev = deferred;
      deferred = null;
      await ev.prompt();
      const { outcome } = await ev.userChoice;
      notify();
      return outcome === 'accepted';
    },
  };
};
